import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";
import { Shell, Head, TotalRow, SubMenu, useEsc, useRowNav, navRowCls, navRowProps, fmtV, fmtD } from "./ReportShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOKS = [
  { key: "sales", label: "Sales Register" },
  { key: "purchase", label: "Purchase Register" },
  { key: "receipt", label: "Receipt Register", gapBefore: true },
  { key: "payment", label: "Payment Register" },
  { key: "journal", label: "Journal Register (All Vouchers)" },
  { key: "cash", label: "Cash Book", gapBefore: true },
  { key: "bank", label: "Bank Book" },
  { key: "quit", label: "Quit", gapBefore: true },
];
const TITLES = { sales: "Sales Register", purchase: "Purchase Register", receipt: "Receipt Register",
  payment: "Payment Register", journal: "Journal Register", cash: "Cash Book", bank: "Bank Book" };
const twoCol = (b) => ["cash", "bank", "journal"].includes(b);

function MonthDetail({ book, month, onBack }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  useEsc(onBack);
  const open = useCallback((r) => {
    if (r.src === "sales" && r.id) navigate(`/invoices/${r.id}/edit?return=accbooks`);
    else if (r.src === "purchase" && r.id) navigate(`/purchases?edit=${r.id}&return=accbooks`);
    else if (r.src === "voucher" && r.id) navigate(`/vouchers?edit=${r.id}&type=${r.vtype || "receipt"}&return=accbooks`);
    else if (r.src === "bank") navigate("/bank?return=accbooks");
  }, [navigate]);
  const onEnter = useCallback((i) => { if (data && data.rows[i]) open(data.rows[i]); }, [data, open]);
  const [sel, setSel] = useRowNav(data ? data.rows.length : 0, onEnter);
  useEffect(() => {
    axios.get(`${API}/reports/register`, { params: { book, month } }).then((r) => setData(r.data)).catch(() => {});
  }, [book, month, dfv]);
  if (!data) return null;
  const two = twoCol(book);
  const taxed = book === "sales" || book === "purchase";
  const tsum = (k) => data.rows.reduce((s, r) => s + (r[k] || 0), 0);
  return (
    <Shell title={TITLES[book]} onBack={onBack} testid="register-month-view"
      subtitle={<><span>Vouchers — click to open &amp; edit</span><span data-testid="rm-label">{data.label}</span></>}>
      <Head cols={[{ t: "Date", w: "w-[100px]" }, { t: "Particulars" }, { t: "Vch Type", w: "w-[100px]", center: true },
        { t: "Vch No.", w: "w-[110px]", center: true },
        ...(taxed ? [{ t: "CGST", w: "w-[110px]", right: true }, { t: "SGST", w: "w-[110px]", right: true }, { t: "IGST", w: "w-[110px]", right: true }] : []),
        ...(two ? [{ t: "Received", w: "w-[140px]", right: true }, { t: "Paid", w: "w-[140px]", right: true }]
                : [{ t: "Amount", w: "w-[160px]", right: true }])]} />
      <div className="flex-1 overflow-y-auto" data-testid="rm-rows">
        {data.rows.map((r, i) => (
          <div key={i} onClick={() => open(r)} {...navRowProps(i, sel, setSel)}
            className={`flex items-center text-sm text-slate-900 cursor-pointer ${navRowCls(sel === i)}`} data-testid={`rm-row-${i}`}>
            <div className="w-[100px] px-2 py-0.5">{fmtD(r.date)}</div>
            <div className="flex-1 px-2 py-0.5 font-semibold">{r.name}</div>
            <div className="w-[100px] px-2 py-0.5 text-center">{r.vch_type}</div>
            <div className="w-[110px] px-2 py-0.5 text-center">{r.vch_no}</div>
            {taxed && (<>
              <div className="w-[110px] px-2 py-0.5 text-right text-slate-600">{r0(r.cgst)}</div>
              <div className="w-[110px] px-2 py-0.5 text-right text-slate-600">{r0(r.sgst)}</div>
              <div className="w-[110px] px-2 py-0.5 text-right text-slate-600">{r0(r.igst)}</div>
            </>)}
            {two ? (<>
              <div className="w-[140px] px-2 py-0.5 text-right">{r.in_amt ? fmtV(r.in_amt) : ""}</div>
              <div className="w-[140px] px-2 py-0.5 text-right">{r.out_amt ? fmtV(r.out_amt) : ""}</div>
            </>) : (
              <div className="w-[160px] px-2 py-0.5 text-right">{r0(r.in_amt + r.out_amt)}</div>
            )}
          </div>
        ))}
        {data.rows.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-500">No vouchers in this month</div>}
      </div>
      <TotalRow testid="rm-totals" cells={[{ t: "Totals" },
        ...(taxed ? [{ t: r0(tsum("cgst")), w: "w-[110px]", right: true }, { t: r0(tsum("sgst")), w: "w-[110px]", right: true }, { t: r0(tsum("igst")), w: "w-[110px]", right: true }] : []),
        ...(two ? [{ t: r0(data.totals.in_amt), w: "w-[140px]", right: true }, { t: r0(data.totals.out_amt), w: "w-[140px]", right: true }]
                : [{ t: r0(data.totals.in_amt + data.totals.out_amt), w: "w-[160px]", right: true }])]} />
    </Shell>
  );
}
const r0 = (n) => (n ? fmtV(n) : "");

function Register({ book, onBack }) {
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  const [month, setMonth] = useState("");
  useEffect(() => {
    axios.get(`${API}/reports/register`, { params: { book } }).then((r) => setData(r.data)).catch(() => {});
  }, [book, dfv]);
  const back = useCallback(() => (month ? setMonth("") : onBack()), [month, onBack]);
  useEsc(back);
  const onEnter = useCallback((i) => {
    if (!month && data && data.rows[i]) setMonth(data.rows[i].month);
  }, [month, data]);
  const [sel, setSel] = useRowNav(month || !data ? 0 : data.rows.length, onEnter);
  if (month) return <MonthDetail book={book} month={month} onBack={() => setMonth("")} />;
  if (!data) return null;
  const two = twoCol(book);
  return (
    <Shell title={TITLES[book]} onBack={back} testid={`register-view-${book}`}
      subtitle={<><span>Month-wise summary — click a month for vouchers</span><span>{data.period}</span></>}>
      <Head cols={[{ t: "Month" }, { t: "Vouchers", w: "w-[110px]", center: true },
        ...(two ? [{ t: "Received", w: "w-[170px]", right: true }, { t: "Paid", w: "w-[170px]", right: true }]
                : [{ t: "Amount", w: "w-[190px]", right: true }])]} />
      <div className="flex-1 overflow-y-auto" data-testid="rg-rows">
        {data.rows.map((r, i) => (
          <div key={r.month} onClick={() => setMonth(r.month)} {...navRowProps(i, sel, setSel)}
            className={`flex items-center text-sm text-slate-900 cursor-pointer ${navRowCls(sel === i)}`} data-testid={`rg-row-${r.month}`}>
            <div className="flex-1 px-3 py-0.5 font-semibold">{r.label}</div>
            <div className="w-[110px] px-2 py-0.5 text-center">{r.count || ""}</div>
            {two ? (<>
              <div className="w-[170px] px-2 py-0.5 text-right">{r0(r.in_amt)}</div>
              <div className="w-[170px] px-2 py-0.5 text-right">{r0(r.out_amt)}</div>
            </>) : (
              <div className="w-[190px] px-2 py-0.5 text-right">{r0(r.in_amt + r.out_amt)}</div>
            )}
          </div>
        ))}
      </div>
      <TotalRow testid="rg-totals" cells={[{ t: "Grand Total" }, { t: String(data.totals.count || ""), w: "w-[110px]", right: true },
        ...(two ? [{ t: r0(data.totals.in_amt), w: "w-[170px]", right: true }, { t: r0(data.totals.out_amt), w: "w-[170px]", right: true }]
                : [{ t: r0(data.totals.in_amt + data.totals.out_amt), w: "w-[190px]", right: true }])]} />
    </Shell>
  );
}

export default function AccountBooksView({ onClose }) {
  const [book, setBook] = useState("");
  if (book) return <Register book={book} onBack={() => setBook("")} />;
  return <SubMenu title="Account Books" items={BOOKS} onPick={setBook} onBack={onClose} testid="account-books-menu" />;
}
